import React, { useEffect, useState, useContext, useRef } from "react";
import { Offcanvas, Button, Spinner } from "react-bootstrap";
import { DateRangePicker } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import StarekContext from "../../context/StarekContext";
import ConfirmModal from "../../components/modals/ConfirmModal";
import { longDateString } from "../../utils";

const JOB_MESSAGES = {
    DELETE_JOB: (jobName) =>
        `Are you sure you want to delete the job "${jobName}"?

Once deleted, the information cannot be retrieved.`,
};

const JobSchedulesSidebar = ({
    isOpen,
    onClose,
    onSubmit,
    type = "add",
    jobData = null,
}) => {
    const starekContext = useContext(StarekContext);
    const [formData, setFormData] = useState({
        name: "",
        part: "",
        cycle: "",
        device: "",
        dateRange: [new Date(), new Date()],
    });
    const [jobParts, setJobParts] = useState([]);
    const [jobCycles, setJobCycles] = useState([]);
    const [jobDevices, setJobDevices] = useState([]);
    const [loadingParts, setLoadingParts] = useState(false);
    const [loadingCycles, setLoadingCycles] = useState(false);
    const [loadingDevices, setLoadingDevices] = useState(false);
    const [partsLoaded, setPartsLoaded] = useState(false);
    const [cyclesLoaded, setCyclesLoaded] = useState(false);
    const [devicesLoaded, setDevicesLoaded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [confirmCfg, setConfirmCfg] = useState({ open: false });
    const [deleting, setDeleting] = useState(false);
    const previousNameRef = useRef("");

    const lastUsedPlant = starekContext.lastUsedPlant;
    const lastUsedWorkspace = starekContext.lastUsedWorkspace;
    const { host, scheme, port } = lastUsedWorkspace.engine;
    const token = starekContext.auth;
    const apiUrl = `${scheme}://${host}:${port}/api/v1/${lastUsedWorkspace.name}/graphql`;

    const openConfirm = (cfg) => setConfirmCfg({ open: true, ...cfg });
    const closeConfirm = () => setConfirmCfg({ open: false });

    useEffect(() => {
        if (isOpen) {
            if (type === "edit" && jobData) {
                const start = new Date(jobData.start_time);
                const end = new Date(jobData.end_time);
                setFormData({
                    name: jobData.name || "",
                    part: jobData.part_id || "",
                    cycle: jobData.cycle_id || "",
                    device: jobData.device_id || "",
                    dateRange: [start, end],
                });
            } else {
                setFormData({
                    name: "",
                    part: "",
                    cycle: "",
                    device: "",
                    dateRange: [new Date(), new Date()],
                });
            }
            // Reset loaded flags and fetch all data when sidebar opens
            setPartsLoaded(false);
            setCyclesLoaded(false);
            setDevicesLoaded(false);
            fetchDevices();
            fetchParts();
            fetchCycles();
        }
    }, [isOpen, type, jobData]);

    const fetchParts = async () => {
        if (partsLoaded || loadingParts) return;
        setLoadingParts(true);
        try {
            const query = {
                query: `
        {
          parts(where: { plant_id: { equals: ${lastUsedPlant.id} } }) {
            id
            name
            plant_id
          }
        }
      `,
            };
            const response = await fetch(apiUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(query),
            });
            const result = await response.json();
            setJobParts(result.data?.parts || []);
            setPartsLoaded(true);
        } catch (error) {
            console.error("Error fetching job parts:", error);
            setJobParts([]);
        } finally {
            setLoadingParts(false);
        }
    };

    // Remove the automatic initialization - data loads on demand

    useEffect(() => {
        // Auto-generate name when any field changes (but not if editing)
        if (
            type !== "edit" &&
            jobDevices.length > 0 &&
            jobParts.length > 0 &&
            jobCycles.length > 0
        ) {
            const selectedDevice = jobDevices.find(
                (d) => d.id === formData.device
            );
            const selectedPart = jobParts.find(
                (p) => String(p.id) === String(formData.part)
            );
            const selectedCycle = jobCycles.find(
                (c) => String(c.id) === String(formData.cycle)
            );
            const [start, end] = formData.dateRange;

            let generatedName = "";

            if (selectedDevice) {
                generatedName = `${selectedDevice.id}`;
            }

            if (selectedPart) {
                generatedName += ` recording ${selectedPart.name}`;
            }

            if (selectedCycle) {
                generatedName += ` on cycle ${selectedCycle.name}`;
            }

            if (start && end) {
                const startDate = longDateString(start);
                const endDate = longDateString(end);
                generatedName += ` between ${startDate} and ${endDate}`;
            }

            // Only update if name actually changed to avoid infinite loops
            if (generatedName !== previousNameRef.current) {
                previousNameRef.current = generatedName;
                setFormData((prev) => ({ ...prev, name: generatedName }));
            }
        }
    }, [
        formData.device,
        formData.part,
        formData.cycle,
        formData.dateRange,
        type,
        jobDevices,
        jobParts,
        jobCycles,
    ]);

    const fetchCycles = async () => {
        if (cyclesLoaded || loadingCycles) return;
        setLoadingCycles(true);
        try {
            const query = {
                query: `
        {
          cycles(where: { plant_id: { equals: ${lastUsedPlant.id} } }) {
            id
            name
            plant_id
          }
        }
      `,
            };
            const response = await fetch(apiUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(query),
            });
            const result = await response.json();
            setJobCycles(result.data?.cycles || []);
            setCyclesLoaded(true);
        } catch (error) {
            console.error("Error fetching job cycles:", error);
            setJobCycles([]);
        } finally {
            setLoadingCycles(false);
        }
    };

    const fetchDevices = async () => {
        if (devicesLoaded || loadingDevices) return;
        setLoadingDevices(true);
        try {
            const query = {
                query: `
        {
          shadowDevices(where: { plant_id: { equals: ${lastUsedPlant.id} } }) {
            id
            name
            installed_at_zones {
              name
            }
          }
        }
      `,
            };
            const response = await fetch(apiUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(query),
            });
            const result = await response.json();
            setJobDevices(result.data?.shadowDevices || []);
            setDevicesLoaded(true);
        } catch (error) {
            console.error("Error fetching devices:", error);
            setJobDevices([]);
        } finally {
            setLoadingDevices(false);
        }
    };

    const postFormData = async (data) => {
        setLoading(true);
        try {
            if (!lastUsedWorkspace || !lastUsedWorkspace.engine) return;
            const query = {
                query: `
    mutation CreateOneJobSchedule {
      createOneJobSchedule(data: {
        name: "${data.name}"
        start_time: "${data.start.toISOString()}"
        end_time: "${data.end.toISOString()}"
        plant: {
          connect: { id: ${lastUsedPlant.id} }
        }
        part: {
          connect: { id: ${data.part} }
        }
        cycle: {
          connect: { id: ${data.cycle} }
        }
        device: {
          connect: { id: "${data.device}" }
        }
        created_by: {
          connect: { id: ${starekContext.user.id} }
        }
        updated_by: {
          connect: { id: ${starekContext.user.id} }
        }
      }) {
        id
        name
        start_time
        end_time
      }
    }
  `,
            };

            const response = await fetch(apiUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(query),
            });
            const result = await response.json();
        } catch (error) {
            console.error("Error creating job schedule:", error);
        } finally {
            setLoading(false);
        }
    };

    const updateFormData = async (data) => {
        setLoading(true);
        try {
            const query = {
                query: `
        mutation UpdateOneJobSchedule {
          updateOneJobSchedule(
            where: { id: ${jobData.id} }
            data: {
              name: { set: "${data.name}" }
              start_time: { set: "${data.start.toISOString()}" }
              end_time: { set: "${data.end.toISOString()}" }
              part: { connect: { id: ${data.part} } }
              cycle: { connect: { id: ${data.cycle} } }
              device: { connect: { id: "${data.device}" } }
              updated_by: { connect: { id: ${starekContext.user.id} } }
            }
          ) {
            id
            name
          }
        }
      `,
            };

            const response = await fetch(apiUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(query),
            });

            const result = await response.json();
        } catch (error) {
            console.error("Error updating job schedule:", error);
        } finally {
            setLoading(false);
        }
    };

    const deleteJobSchedule = async () => {
        if (!jobData?.id) return;
        setDeleting(true);
        setError(null);
        try {
            const query = {
                query: `
        mutation DeleteOneJobSchedule {
          deleteOneJobSchedule(where: { id: ${jobData.id} }) {
            id
            name
          }
        }
      `,
            };
            const response = await fetch(apiUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(query),
            });
            const result = await response.json();
            if (result.data?.deleteOneJobSchedule) {
                closeConfirm();
                onClose();
                onSubmit();
            } else {
                throw new Error("Delete failed");
            }
        } catch (err) {
            setError("Failed to delete job schedule");
        } finally {
            setDeleting(false);
        }
    };

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async () => {
        const [start, end] = formData.dateRange;
        setError(null);

        const payload = { ...formData, start, end };

        try {
            if (type === "edit") {
                await updateFormData(payload);
            } else {
                await postFormData(payload);
            }
            onSubmit();
            onClose(); // Close sidebar
        } catch (err) {
            console.error("Form submission error:", err);
            setError("Something went wrong. Please try again.");
        }
    };

    return (
        <>
            <Offcanvas
                show={isOpen}
                onHide={onClose}
                placement="end"
                backdrop={true}
                style={{
                    width: "580px",
                    overflow: "visible",
                    zIndex: 1050,
                }}
            >
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title>
                        <h2 className="tw-text-lg tw-font-semibold">
                            {type === "edit"
                                ? "Edit Job Schedule"
                                : "Add Job Schedule"}
                        </h2>
                    </Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body className="tw-flex tw-flex-col tw-h-full tw-overflow-visible tw-relative">
                    <div className="tw-flex-grow tw-flex tw-flex-col tw-gap-4 tw-overflow-auto">
                        <div>
                            <label className="tw-text-sm tw-font-medium">
                                Name <span className="tw-text-red-500">*</span>
                            </label>
                            <textarea
                                className="tw-w-full tw-border tw-rounded-md tw-p-2"
                                rows="4"
                                value={formData.name}
                                onChange={(e) =>
                                    handleChange("name", e.target.value)
                                }
                            />
                        </div>

                        <div>
                            <label className="tw-text-sm tw-font-medium tw-flex tw-items-center tw-gap-2">
                                Select Device{" "}
                                <span className="tw-text-red-500">*</span>
                                {loadingDevices && (
                                    <Spinner animation="border" size="sm" />
                                )}
                            </label>
                            <select
                                className="tw-w-full tw-border tw-rounded-md tw-p-2"
                                value={formData.device}
                                onChange={(e) =>
                                    handleChange("device", e.target.value)
                                }
                                disabled={loadingDevices}
                            >
                                <option value="">Select Device</option>
                                {jobDevices?.map((d) => (
                                    <option key={d.id} value={d.id}>
                                        {d.id}(
                                        {d.installed_at_zones?.[0]?.name ||
                                            "No Zone"}
                                        )
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="tw-text-sm tw-font-medium tw-flex tw-items-center tw-gap-2">
                                Select Part{" "}
                                <span className="tw-text-red-500">*</span>
                                {loadingParts && (
                                    <Spinner animation="border" size="sm" />
                                )}
                            </label>
                            <select
                                className="tw-w-full tw-border tw-rounded-md tw-p-2"
                                value={formData.part}
                                onChange={(e) =>
                                    handleChange("part", e.target.value)
                                }
                                disabled={loadingParts}
                            >
                                <option value="">Select Part</option>
                                {jobParts?.map((p) => (
                                    <option key={p.id} value={p.id}>
                                        {p.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="tw-text-sm tw-font-medium tw-flex tw-items-center tw-gap-2">
                                Select Cycle{" "}
                                <span className="tw-text-red-500">*</span>
                                {loadingCycles && (
                                    <Spinner animation="border" size="sm" />
                                )}
                            </label>
                            <select
                                className="tw-w-full tw-border tw-rounded-md tw-p-2"
                                value={formData.cycle}
                                onChange={(e) =>
                                    handleChange("cycle", e.target.value)
                                }
                                disabled={loadingCycles}
                            >
                                <option value="">Select Cycle</option>
                                {jobCycles?.map((c) => (
                                    <option key={c.id} value={c.id}>
                                        {c.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="tw-text-sm tw-font-medium">
                                Select Date & Time Range{" "}
                                <span className="tw-text-red-500">*</span>
                            </label>
                            <div className="tw-overflow-visible tw-relative">
                                <DateRangePicker
                                    format="yyyy-MM-dd HH:mm"
                                    value={formData.dateRange}
                                    onChange={(range) => {
                                        if (range) {
                                            handleChange("dateRange", range);
                                        }
                                    }}
                                    block
                                    placement="auto"
                                    container={null}
                                    popupClassName="rsuite-datepicker-override"
                                    style={{ width: "100%" }}
                                />
                            </div>
                        </div>
                    </div>
                    {confirmCfg.open && (
                        <ConfirmModal
                            open={confirmCfg.open}
                            type={confirmCfg.type}
                            title={confirmCfg.title}
                            message={confirmCfg.message}
                            confirmText={confirmCfg.confirmText}
                            cancelText={confirmCfg.cancelText}
                            onConfirm={confirmCfg.onConfirm}
                            onClose={confirmCfg.onClose}
                            isLoading={confirmCfg.isLoading}
                        />
                    )}

                    {error && (
                        <div className="tw-text-red-600 tw-mb-2">{error}</div>
                    )}
                    <div className="tw-flex tw-flex-row tw-justify-between tw-items-center">
                        <div>
                            {type === "edit" && (
                                <Button
                                    variant="danger"
                                    onClick={() =>
                                        openConfirm({
                                            title: "Delete Job?",
                                            message: JOB_MESSAGES.DELETE_JOB(
                                                jobData.name
                                            ),
                                            confirmText: "Delete",
                                            cancelText: "Cancel",
                                            type: "danger",
                                            onConfirm: deleteJobSchedule,
                                            onClose: closeConfirm,
                                            isLoading: deleting,
                                        })
                                    }
                                >
                                    Delete
                                </Button>
                            )}
                        </div>

                        <div className="tw-flex tw-justify-end tw-gap-2 tw-mt-4">
                            <Button variant="outline-danger" onClick={onClose}>
                                Discard
                            </Button>
                            <Button
                                className="tw-px-4 tw-py-2 tw-bg-blue-600 tw-text-white tw-rounded-md tw-inline-flex tw-items-center tw-gap-2"
                                onClick={handleSubmit}
                                disabled={loading}
                            >
                                {loading && (
                                    <svg
                                        className="tw-animate-spin tw-h-4 tw-w-4 tw-text-white"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="tw-opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        ></circle>
                                        <path
                                            className="tw-opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8v8z"
                                        ></path>
                                    </svg>
                                )}
                                {type === "edit" ? "Update" : "Add"}
                            </Button>
                        </div>
                    </div>
                </Offcanvas.Body>
            </Offcanvas>
            <ConfirmModal {...confirmCfg} />
        </>
    );
};

export default JobSchedulesSidebar;
